import type { StockItem } from '@/types'

/**
 * Row-status presentation for the Items table — a thin view over the
 * server-computed `severity` (the ONE definition, classifyStockAlertSeverity
 * on the backend). Never recompute status client-side: that is how three
 * surfaces came to disagree on one screen.
 */

export type ItemSeverity = 'oversold' | 'out' | 'low' | 'ok'

export interface ItemStatusMeta {
  label: string
  /** Tailwind classes for the chip. */
  chipCls: string
  /** Tone for the on-hand figure. */
  qtyCls: string
}

export function itemStatusMeta(item: Pick<StockItem, 'severity' | 'on_order_qty' | 'reorder_level' | 'quantity_on_hand'>): ItemStatusMeta {
  const sev = (item.severity as ItemSeverity) ?? 'ok'
  const onOrder = item.on_order_qty ?? 0
  switch (sev) {
    case 'oversold':
      return {
        label: 'Oversold',
        chipCls: 'bg-danger-surface text-danger-fg border border-danger-border',
        qtyCls: 'text-danger-fg font-semibold',
      }
    case 'out':
      return {
        label: onOrder > 0 ? 'Out · on order' : 'Out of stock',
        chipCls: 'bg-danger-surface text-danger-fg border border-danger-border',
        qtyCls: 'text-danger-fg font-semibold',
      }
    case 'low':
      return {
        label: onOrder > 0 ? 'Low · on order' : 'Low',
        chipCls: 'bg-warning-surface text-warning-fg border border-warning-border',
        qtyCls: 'text-warning-fg font-medium',
      }
    default:
      if ((item.reorder_level ?? 0) <= 0) {
        return {
          label: 'No reorder point',
          chipCls: 'text-muted-foreground/70',
          qtyCls: '',
        }
      }
      return { label: 'OK', chipCls: 'text-success-fg', qtyCls: '' }
  }
}

/**
 * The one visible per-row action. Oversold stock has exactly one honest fix —
 * a physical count; everything else's everyday action is restocking.
 */
export function contextAction(item: Pick<StockItem, 'severity'>): 'count' | 'restock' {
  return item.severity === 'oversold' ? 'count' : 'restock'
}

/**
 * Shared "that's a lot of money" heuristic (same thresholds as the count
 * dialog's loud confirm): flag a value over Rs 5,000. Used by Add Item to
 * demand a second look before creating a six-figure asset in one click —
 * the NOOK 800 kg condensed-milk entry path.
 */
export function isLargeStockValue(qty: number, unitCost: number): boolean {
  return qty > 0 && unitCost > 0 && qty * unitCost > 5000
}
